import { ReactNode } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import type { CollectionTicketClaimMemberStateDTOState } from '@/api/trafikportalen/models/collectionTicketClaimMemberStateDTOState';
import { CLAIM_STATES, SCHEDULED_LABEL } from '@/helpers/claimStates';
import { cn } from '@/helpers/classnames';
import { ticketRevocationMessages } from '@/helpers/ticketRevocation';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { useTenantID } from '@/hooks/useTenantID';

interface InfoSectionProps {
  title: ReactNode;
  children: ReactNode;
}

const InfoSection = ({ title, children }: InfoSectionProps) => (
  <section className="flex flex-col gap-4 rounded-lg border border-background-300 bg-background-50 p-6">
    <h2 className="text-lg font-semibold text-text-900">{title}</h2>
    {children}
  </section>
);

interface InfoListProps {
  children: ReactNode;
}

const InfoList = ({ children }: InfoListProps) => (
  <ul className="flex list-disc flex-col gap-2 pl-4">{children}</ul>
);

interface StepProps {
  title: ReactNode;
  children: ReactNode;
}

const Step = ({ title, children }: StepProps) => (
  <li>
    <span className="font-medium text-text-900">{title}</span>
    <p className="mt-1 text-text-700">{children}</p>
  </li>
);

interface StatusGuide {
  key: string;
  label: MessageDescriptor;
  color: string;
  meaning: ReactNode;
  action?: ReactNode;
}

const claimStateGuide = (
  state: CollectionTicketClaimMemberStateDTOState,
  meaning: ReactNode,
  action?: ReactNode,
): StatusGuide => ({
  key: state,
  label: CLAIM_STATES[state].label,
  color: CLAIM_STATES[state].color,
  meaning,
  action,
});

const PROGRESS_STATUS_GUIDES: StatusGuide[] = [
  {
    key: 'scheduled',
    label: SCHEDULED_LABEL,
    color: 'bg-blue-100 text-blue-800',
    meaning: (
      <FormattedMessage defaultMessage="Eleven ingår i en aktiv period, men systemet har inte börjat beställa än. Det går oftast snabbt, men vid högt tryck kan det ta några dagar." />
    ),
  },
  claimStateGuide(
    'card-ordered',
    <FormattedMessage defaultMessage="Eleven saknar skolkort, så ett skolkort har beställts från kortleverantören. Biljetten beställs när kortet har skapats, och kortet skickas till elevens leveransadress." />,
  ),
  claimStateGuide(
    'awaiting-card-transfer',
    <FormattedMessage defaultMessage="Eleven har flyttats från en annan organisation och tar med sig sitt skolkort. Biljetten köps när kortet har förts över." />,
  ),
  claimStateGuide(
    'purchase-ordered',
    <FormattedMessage defaultMessage="Biljetten är beställd i BobCat och systemet väntar på bekräftelse att den har köpts." />,
  ),
  claimStateGuide(
    'fulfilled',
    <FormattedMessage defaultMessage="Eleven har en biljett för perioden. Tar biljetten slut medan perioden pågår köps en ny automatiskt." />,
  ),
  {
    key: 'revoked',
    label: ticketRevocationMessages.label,
    color: 'bg-yellow-100 text-yellow-800',
    meaning: (
      <FormattedMessage defaultMessage="Periodbiljetten är schemalagd för makulering." />
    ),
  },
];

const ERROR_STATUS_GUIDES: StatusGuide[] = [
  claimStateGuide(
    'card-order-failed',
    <FormattedMessage defaultMessage="Kortleverantören tog inte emot kortbeställningen." />,
    <FormattedMessage defaultMessage="Kontrollera att eleven har en fullständig leveransadress. Systemet gör ett nytt försök automatiskt." />,
  ),
  claimStateGuide(
    'purchase-failed',
    <FormattedMessage defaultMessage="BobCat tog inte emot biljettköpet." />,
    <FormattedMessage defaultMessage="Systemet försöker igen automatiskt. Kontakta support om felet ligger kvar." />,
  ),
  claimStateGuide(
    'unresolvable-relational-ticket',
    <FormattedMessage defaultMessage="Ingen biljett bland periodens biljettkategorier täcker resan mellan elevens adresser och skolan." />,
    <FormattedMessage defaultMessage="Kontrollera adresser och hållplatser. Etis hittar bara hållplatser inom 3 km från en adress, så ligger närmaste hållplats längre bort behöver en hållplats anges. En hållplats måste heta exakt som i Etis. Kontrollera också att periodens biljettkategorier täcker resan." />,
  ),
  claimStateGuide(
    'unfulfillable',
    <FormattedMessage defaultMessage="Felet beror oftast på inställningar och löser sig inte av sig självt." />,
    <FormattedMessage defaultMessage="Kontrollera att eleven har en bärare, och att periodens biljett finns i BobCat och är tillåten för organisationen under Elevhantering. Kontakta support om felet ligger kvar." />,
  ),
];

interface StatusGuideListProps {
  guides: StatusGuide[];
}

const StatusGuideList = ({ guides }: StatusGuideListProps) => {
  const { formatMessage } = useIntl();

  return (
    <ul className="flex flex-col gap-4">
      {guides.map(({ key, label, color, meaning, action }) => (
        <li key={key} className="flex flex-col items-start gap-1">
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              color,
            )}
          >
            {formatMessage(label)}
          </span>
          <p className="text-text-800">{meaning}</p>
          {action && <p className="text-text-700">{action}</p>}
        </li>
      ))}
    </ul>
  );
};

export const StudentPortalInfo = () => {
  const tenantId = useTenantID();
  const {
    claimType,
    schoolCardsArePaid,
    disableDiscountCodes,
    disableHalfProduct,
  } = useTenantConfiguration();

  return (
    <>
      <InfoSection
        title={
          <FormattedMessage defaultMessage="Elever: kom igång med en organisation" />
        }
      >
        <p className="text-text-700">
          <FormattedMessage defaultMessage="Organisationerna sköter sina elever själva, men några steg kan bara en systemadministratör göra. Så här kommer en organisation igång med biljetter och kort till sina elever." />
        </p>
        <ol className="flex list-decimal flex-col gap-3 pl-5">
          <Step
            title={
              <FormattedMessage defaultMessage="Aktivera elevkategorier" />
            }
          >
            <FormattedMessage defaultMessage="Elevkategorier aktiveras aldrig när en organisation skapas eller en ansökan godkänns. Öppna organisationen under Organisationer och gå till fliken Elevhantering för att aktivera Elevresor, Skolskjuts eller Barn- & ungdomskort." />
          </Step>
          <Step
            title={<FormattedMessage defaultMessage="Bestäm periodåtkomst" />}
          >
            <FormattedMessage defaultMessage="Under Periodåtkomst bestämmer du om organisationen får hantera sina egna perioder (mer om perioder nedan). Med periodåtkomst väljer du under varje kategori vilka statiska biljetter och biljettkategorier organisationen får använda. Utan periodåtkomst är det perioderna du skapar som avgör vilka biljetter organisationen får." />
          </Step>
          {!disableDiscountCodes && (
            <Step
              title={<FormattedMessage defaultMessage="Ange rabattkoder" />}
            >
              <FormattedMessage defaultMessage="Kategorins rabattkod från BobCat används automatiskt när systemet köper biljetter i kategorin." />
              {!disableHalfProduct && (
                <>
                  {' '}
                  <FormattedMessage defaultMessage="Koden för halv produkt används istället för elever som är markerade med Halv produkt. Organisationen kan bara markera elever med Halv produkt när en sådan kod är angiven." />
                </>
              )}
            </Step>
          )}
          <Step title={<FormattedMessage defaultMessage="Skapa en period" />}>
            <FormattedMessage defaultMessage="Har organisationen inte periodåtkomst skapar du perioderna under Perioder i menyn. Utan en aktiv eller kommande period beställs varken biljetter eller kort." />
          </Step>
          <Step
            title={
              <FormattedMessage defaultMessage="Elever och administratörer" />
            }
          >
            <FormattedMessage defaultMessage="Organisationen lägger till elever under respektive kategori, oftast genom import. Administratörer kan få åtkomst till enskilda kategorier under Administratörer. Portalen skickar inga e-postmeddelanden till importerade elever, så organisationen behöver själv berätta om självbetjäningen." />
          </Step>
          <Step title={<FormattedMessage defaultMessage="Följ upp" />}>
            <FormattedMessage defaultMessage="Siffran vid Perioder i menyn och varningarna på startsidan visar organisationer som saknar en aktiv eller kommande period. Fel i biljettbeställningen syns på organisationens startsida och i elevlistan, som du når via Visa som." />
          </Step>
        </ol>
      </InfoSection>

      <InfoSection title={<FormattedMessage defaultMessage="Perioder" />}>
        <InfoList>
          <li>
            <FormattedMessage defaultMessage="Under Perioder i menyn skapar du samma period för flera organisationer på en gång: välj kategori, markera organisationerna och klicka på Skapa period." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Perioder med samma datum och inställningar visas som en grupp. Har du markerat organisationer i listan påverkar ändringar och borttagning bara dem, annars alla organisationer i gruppen." />
          </li>
          <li>
            <FormattedMessage defaultMessage="En relationsbunden period köper den billigaste biljetten som täcker resan mellan vårdnadshavarnas adresser (eller hållplatser) och skolan, bland de valda biljettkategorierna. En statisk period ger alla elever samma biljett. Barn- & ungdomskort använder alltid statiska biljetter." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Biljettsöket i Etis hittar bara hållplatser inom 3 km från en adress. Ligger närmaste hållplats längre bort behöver en hållplats anges istället." />
          </li>
          <li>
            <FormattedMessage defaultMessage="När du skapar en period läggs den valda biljetten eller biljettkategorierna till i organisationernas inställningar under Elevhantering." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Slutdatumet räknas med, så perioden gäller dagen ut. Väljer du dagens datum som startdatum börjar perioden direkt." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Kommande perioder kan ändras och tas bort. En aktiv period kan bara få ett nytt slutdatum." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Makuleringsstrategin avgör vad som händer med biljetterna när perioden tar slut: Makulera tar bort dem och Inget låter dem gälla vidare. Elever som inaktiveras eller tas bort får alltid sina biljetter makulerade." />
          </li>
          <li>
            <FormattedMessage defaultMessage="En kostnadsfri period skapar inga transaktioner, så biljetterna faktureras inte. Bara systemadministratörer kan skapa kostnadsfria perioder." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Perioder får överlappa, men då kan elever få biljetter från flera perioder samtidigt." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Vid högt tryck kan kort och biljetter ta flera dagar att leverera. Låt perioden börja minst en vecka innan biljetterna behöver gälla." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Nya biljetter i BobCat synkas varje natt och kan väljas först dagen efter." />
          </li>
        </InfoList>
      </InfoSection>

      <InfoSection
        title={<FormattedMessage defaultMessage="Biljetter och kort" />}
      >
        <InfoList>
          <li>
            <FormattedMessage defaultMessage="Systemet beställer biljetter och kort automatiskt medan en period är aktiv. Det går oftast snabbt, men vid högt tryck kan det ta några dagar." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Alla elever måste ha ett resekort. Saknar eleven kort beställs ett innan biljetten köps, och kortet skickas till elevens leveransadress. Systemet beställer bara ett kort per elev." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Tar en biljett slut under en aktiv period köps en ny automatiskt." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Ändras en adress i en relationsbunden period köps en ny biljett och den gamla makuleras." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Biljetter från en period kan inte avslutas manuellt. Inaktivera eleven om biljetterna ska bort direkt, annars ändra perioden." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Tappar eleven sitt kort kan eleven byta bärare i självbetjäningen, eller så byter en administratör skolkortet under resenärens bärare. Aktiva och kommande biljetter flyttas då till det nya kortet." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Ett skolkort måste bytas ut innan det kan rensas under Sök resenärer." />
          </li>
        </InfoList>
      </InfoSection>

      <InfoSection title={<FormattedMessage defaultMessage="Biljettstatus" />}>
        <p className="text-text-700">
          <FormattedMessage defaultMessage="Varje elev i en aktiv period har en biljettstatus. Den syns i elevlistan, där du också kan filtrera på den, och som en tidslinje under elevens Biljettstatus. Elever med fel visas även på organisationens startsida. Öppna organisationen med Visa som, eller sök upp eleven under Sök resenärer." />
        </p>
        <h3 className="text-base font-semibold text-text-900">
          <FormattedMessage defaultMessage="Pågående och klart" />
        </h3>
        <p className="text-text-700">
          <FormattedMessage defaultMessage="De här statusarna betyder att systemet arbetar med eleven eller är klart, och du behöver inte göra något. En elev går normalt igenom dem i ordning. Har eleven redan ett kort hoppas kortstegen över." />
        </p>
        <StatusGuideList guides={PROGRESS_STATUS_GUIDES} />
        <h3 className="text-base font-semibold text-text-900">
          <FormattedMessage defaultMessage="Fel" />
        </h3>
        <p className="text-text-700">
          <FormattedMessage defaultMessage="Under elevens Biljettstatus finns också ett internt felmeddelande och hur många gånger i rad systemet har försökt utan att lyckas." />
        </p>
        <StatusGuideList guides={ERROR_STATUS_GUIDES} />
      </InfoSection>

      <InfoSection
        title={<FormattedMessage defaultMessage="Elever och organisationer" />}
      >
        <InfoList>
          <li>
            <FormattedMessage defaultMessage="Som systemadministratör öppnar du inte elever direkt. Använd Visa som på organisationen, eller sök på namn, personnummer eller kortnummer under Sök resenärer och klicka på eleven." />
          </li>
          <li>
            <FormattedMessage defaultMessage="I Visa som följer organisationens Perioder-flik organisationens periodåtkomst. Saknar organisationen periodåtkomst ändrar du perioderna under Perioder i menyn." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Ett personnummer kan bara vara aktivt i en organisation. Läggs en elev till som är inaktiv i en annan organisation flyttas eleven över, med sitt skolkort. Är eleven aktiv, eller schemalagd för inaktivering, i den andra organisationen nekas det." />
          </li>
          <li>
            <FormattedMessage defaultMessage="En import ersätter hela kategorin. Elever som saknas i filen inaktiveras." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Schemaläggs en inaktivering makuleras elevens periodbiljetter direkt, inte först på inaktiveringsdatumet." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Inaktiverar du en elevkategori för en organisation raderas alla elever i kategorin direkt och deras biljetter makuleras. Aktiverar du kategorin igen är den tom." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Inaktiveras en organisation makuleras alla aktiva och oanvända biljetter. De kommer inte tillbaka om organisationen aktiveras igen." />
          </li>
        </InfoList>
      </InfoSection>

      <InfoSection
        title={
          <FormattedMessage defaultMessage="Fakturering av elevbiljetter" />
        }
      >
        <InfoList>
          {claimType === 'single' ? (
            <li>
              <FormattedMessage defaultMessage="Elevbiljetter faktureras månadsvis efter att de köpts." />
              {tenantId === 'llt' && (
                <>
                  {' '}
                  <FormattedMessage defaultMessage="Köpta biljetter blir billigare ju fler som köpts. Återställs mellan terminerna." />
                </>
              )}
            </li>
          ) : (
            <li>
              <FormattedMessage defaultMessage="Elevbiljetter faktureras månadsvis i efterhand. Varje elev som haft en utställd biljett under föregående månad debiteras för en hel månad, oavsett om biljetten använts eller hur stor del av månaden den gällde. Har biljetten bytts på grund av adressbyte tas den dyraste betalt." />
            </li>
          )}
          <li>
            <FormattedMessage defaultMessage="Transaktioner skapas utifrån aktiva perioder, inte utifrån om eleven faktiskt har fått sin biljett. En elev kan därför ha transaktioner trots att biljetten inte har kommit fram än, t.ex. för att eleven väntar på att skolkortet ska levereras." />
          </li>
          {schoolCardsArePaid && (
            <li>
              <FormattedMessage defaultMessage="Beställda skolkort faktureras månadsvis." />
            </li>
          )}
          <li>
            <FormattedMessage defaultMessage="Elevkategorierna har ingen kreditgräns. En kategori kan få egen faktureringsinformation under fliken Fakturering, annars används organisationens." />
          </li>
        </InfoList>
      </InfoSection>
    </>
  );
};
